import { isDealActive } from '@/lib/utils';
import { AssignedTask, Task } from '@/models/registry';
import type {
  AssignedTask as AssignedTaskType,
  Task as TaskType,
} from '@/models/types';
import { makeAutoObservable, ObservableMap } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';
import { dealStore } from './deals';

class TasksStore implements BaseStore<Task, TaskType> {
  store: Map<string, Task> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  getById(id: string): Task | undefined {
    return this.store.get(id);
  }

  add(input: TaskType) {
    const task = new Task(input);
    this.store.set(task.id, task);
  }

  removeTask(id: TaskType['id']) {
    this.store.delete(id);
  }

  // computed getter for all tasks
  get all(): Task[] {
    return Array.from(this.store.values());
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<TaskType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TaskType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_tasks.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
    }

    this.upsert(delta.data);
  }

  upsert(task: TaskType) {
    const existingTask = this.store.get(task.id);
    if (existingTask) {
      this.replace({ id: task.id, task });
    } else {
      this.add(task);
    }
  }

  replace({ id, task }: { id: string; task: TaskType }) {
    const existingTask = this.store.get(id);
    if (!existingTask) return;
    existingTask.description = task.description;
    existingTask.title = task.title;

    this.save(existingTask);
  }

  save(task: Task) {
    this.store.set(task.id, task);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_tasks',
      entity: 'deal_tasks',
      modelStore: taskStore,
    });
  }
}

export const taskStore = new TasksStore();

class AssignedTasksStore implements BaseStore<AssignedTask, AssignedTaskType> {
  store: ObservableMap<string, AssignedTask> = new ObservableMap();

  storeIndexedByForeignKey: ObservableMap<string, AssignedTask[]> =
    new ObservableMap();

  constructor() {
    makeAutoObservable(this);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<AssignedTaskType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AssignedTaskType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (hydrateIndexedDB) {
      db.deal_task_assignments.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ taskId: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ taskId: delta.record_id, dealId: delta.data.deal_id });
      db.deal_task_assignments.delete(delta.record_id);
      return;
    }

    this.upsertAssignedTask({
      assignedTask: delta.data,
      deal_id: delta.data.deal_id,
    });
  }

  getById(id: string): AssignedTask | undefined {
    return this.store.get(id);
  }

  add(input: AssignedTaskType) {
    const assignedTask = new AssignedTask(input);
    this.store.set(assignedTask.id, assignedTask);

    const arr = this.storeIndexedByForeignKey.get(assignedTask.deal_id) || [];
    arr.push(assignedTask);
    this.storeIndexedByForeignKey.set(assignedTask.deal_id, arr);
  }

  // computed getter for all tasks
  get all(): AssignedTask[] {
    return Array.from(this.store.values());
  }

  get allForActiveDeals(): AssignedTask[] {
    return this.all
      .filter((task) => {
        const deal = dealStore.getById(task.deal_id);
        return isDealActive(deal);
      })
      .sort((a, b) => {
        // First, sort by status (incomplete first, then done)
        if (a.status === 'incomplete' && b.status === 'done') return -1;
        if (a.status === 'done' && b.status === 'incomplete') return 1;

        // Within the same status, sort by assignment (unassigned first, then assigned)
        const aIsUnassigned = !a.assignee_id;
        const bIsUnassigned = !b.assignee_id;

        if (aIsUnassigned && !bIsUnassigned) return -1;
        if (!aIsUnassigned && bIsUnassigned) return 1;

        return 0;
      });
  }

  remove({ taskId, dealId }: { taskId: string; dealId: string }) {
    const assignedTasks = this.storeIndexedByForeignKey.get(dealId) || [];
    const index = assignedTasks.findIndex((task) => task.id === taskId);
    if (index === -1) return;

    assignedTasks.splice(index, 1);
    this.storeIndexedByForeignKey.set(dealId, [...assignedTasks]);

    this.store.delete(taskId);
  }

  updateStatus({
    id,
    status,
  }: {
    id: string;
    status: AssignedTaskType['status'];
  }) {
    const assignedTask = this.store.get(id);
    if (!assignedTask) return;

    assignedTask.status = status;
  }

  updateAssignee({
    id,
    assignee_id,
  }: {
    id: string;
    assignee_id: string | null | undefined;
  }) {
    const assignedTask = this.store.get(id);
    if (!assignedTask) return;

    assignedTask.assignee_id = assignee_id;
  }

  upsertAssignedTask({
    assignedTask,
    deal_id,
  }: {
    assignedTask: AssignedTaskType;
    deal_id: string;
  }) {
    const existingAssignedTask = this.store.get(assignedTask.id);
    if (existingAssignedTask) {
      this.updateStatus({
        id: assignedTask.id,
        status: assignedTask.status,
      });

      this.updateAssignee({
        id: assignedTask.id,
        assignee_id: assignedTask.assignee_id,
      });
      this.save(existingAssignedTask);
    } else {
      assignedTask.deal_id = deal_id;
      this.add(assignedTask);
    }
  }

  save(assignedTask: AssignedTask) {
    this.store.set(assignedTask.id, assignedTask);

    const assignedTasks =
      this.storeIndexedByForeignKey.get(assignedTask.deal_id) || [];
    const index = assignedTasks.findIndex((t) => t.id === assignedTask.id);
    if (index !== -1) {
      assignedTasks[index] = assignedTask;
    }

    this.storeIndexedByForeignKey.set(assignedTask.deal_id, [...assignedTasks]);
  }

  upsert(assignedTask: AssignedTaskType) {
    this.upsertAssignedTask({ assignedTask, deal_id: assignedTask.deal_id });
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_task_assignments',
      entity: 'deal_task_assignments',
      modelStore: assignedTaskStore,
    });
  }

  async hydrateDeal(dealId: AssignedTaskType['deal_id']): Promise<void> {
    const items = await db.deal_task_assignments.toArray();
    items.forEach((item) => {
      if (item.deal_id === dealId) this.upsert(item);
    });
  }
}

export const assignedTaskStore = new AssignedTasksStore();
